/**
 * Inventaire guidé par zone (/cave/inventaire) : choix d'une zone, comptage casier par casier,
 * motif d'écart obligatoire, validation → mouvements `inventaire` ramenant le stock au compté.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { inventoryZones, inventoryReferences, inventoryValidate } from '../../offline/store';
import { Spinner, Button, EmptyState } from '../../components/ui/kit';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { WineColorDot } from '../../components/wine/wine';

const ECART_MOTIFS = [
  { key: 'casse', label: 'Casse' }, { key: 'erreur_sortie', label: 'Erreur de sortie' },
  { key: 'vol', label: 'Vol / perte' }, { key: 'mal_rangee', label: 'Mal rangée' }, { key: 'autre', label: 'Autre' },
];

export default function Inventaire() {
  const qc = useQueryClient();
  const [zone, setZone] = useState(null);
  const [idx, setIdx] = useState(0);
  const [counts, setCounts] = useState({}); // reference_id -> {counted, motif}
  const [done, setDone] = useState(false);

  const { data: zones } = useQuery({ queryKey: ['inv-zones'], queryFn: inventoryZones });
  const { data: refs = [] } = useQuery({ queryKey: ['inv-refs', zone], queryFn: () => inventoryReferences(zone), enabled: !!zone });

  if (!zones) return <Spinner />;

  // Sélection de zone.
  if (!zone) {
    return (
      <div className="p-4 md:p-6 max-w-[700px]">
        <p className="kicker mb-1">Inventaire guidé</p>
        <h2 className="font-title text-3xl mb-1">Choisir une zone</h2>
        <p className="text-[13px] text-n500 mb-4">Dernier inventaire : {zones.last_inventory ? new Date(zones.last_inventory).toLocaleDateString('fr-FR') : '—'}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {zones.zones.map((z) => (
            <button key={z.code} onClick={() => { setZone(z.code); setIdx(0); setCounts({}); setDone(false); }}
              className="vd-card p-4 text-left hover:bg-surface" data-testid={`zone-${z.code}`}>
              <span className="font-title text-xl">{z.code}</span>
              <span className="block text-[12px] text-n500">{z.references} références · {z.bottles} btl</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const current = refs[idx];
  const entry = current ? counts[current.reference_id] || { counted: current.theoretical, motif: null } : null;
  const ecart = current ? entry.counted - current.theoretical : 0;

  const setEntry = (patch) => setCounts((c) => ({ ...c, [current.reference_id]: { ...entry, ...patch } }));
  const next = () => setIdx((i) => Math.min(i + 1, refs.length));

  const validate = async () => {
    const payload = { zone, counts: Object.entries(counts).map(([reference_id, v]) => ({ reference_id, counted: v.counted, motif: v.motif })) };
    const res = await inventoryValidate(payload);
    toast.success(`Inventaire validé · ${res.count} écart(s)${res.offline ? ' · hors-ligne' : ''}`);
    qc.invalidateQueries({ queryKey: ['stock'] });
    qc.invalidateQueries({ queryKey: ['alerts'] });
    setZone(null);
  };

  // Récapitulatif final.
  if (idx >= refs.length || done) {
    const ecarts = Object.entries(counts).filter(([rid, v]) => { const r = refs.find((x) => x.reference_id === rid); return r && v.counted !== r.theoretical; });
    return (
      <div className="p-4 md:p-6 max-w-[700px]" data-testid="inv-summary">
        <p className="kicker mb-1">Zone {zone}</p>
        <h2 className="font-title text-3xl mb-4">Récapitulatif</h2>
        {ecarts.length === 0 ? <EmptyState title="Aucun écart" description="Tout est conforme." /> : (
          <div className="vd-card mb-4">
            {ecarts.map(([rid, v]) => { const r = refs.find((x) => x.reference_id === rid); const d = v.counted - r.theoretical; return (
              <div key={rid} className="flex justify-between py-2 px-3 hair-b last:border-b-0 text-[14px]">
                <span>{r.name} {r.year} · {r.location_code}</span>
                <span className="nums text-gold700">{d > 0 ? '+' : ''}{d} · {ECART_MOTIFS.find((m) => m.key === v.motif)?.label || '—'}</span>
              </div>
            ); })}
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={() => setZone(null)}>Changer de zone</Button>
          <Button variant="primary" onClick={validate} data-testid="inv-validate">Valider l'inventaire</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[700px]" data-testid="inv-walk">
      <div className="flex items-center justify-between mb-2">
        <p className="kicker">Zone {zone} · {idx + 1}/{refs.length}</p>
        <Button onClick={() => setDone(true)} className="h-8">Terminer</Button>
      </div>
      <div className="h-1 bg-surface rounded mb-5"><div className="h-1 bg-gold rounded" style={{ width: `${((idx) / refs.length) * 100}%` }} /></div>

      {current && (
        <div className="vd-card p-5" data-testid={`inv-casier-${current.reference_id}`}>
          <div className="flex items-center gap-2">
            <WineColorDot color={current.color} />
            <span className="font-title text-2xl">{current.name} {current.year}</span>
          </div>
          <p className="text-[13px] text-n600">{current.format_label} · casier {current.location_code}</p>
          <p className="text-[13px] text-n500 mt-1">Quantité théorique : <span className="nums">{current.theoretical}</span></p>

          <div className="flex items-center gap-4 my-4">
            <QuantityStepper value={entry.counted} min={0} onChange={(v) => setEntry({ counted: v })} shortcuts={false} size="lg" testId="inv-count" />
            {ecart !== 0 && <span className="nums text-2xl text-gold700" data-testid="inv-ecart">{ecart > 0 ? '+' : ''}{ecart}</span>}
          </div>

          {ecart !== 0 && (
            <div className="mb-4">
              <p className="kicker mb-2">Motif de l'écart (obligatoire)</p>
              <div className="flex flex-wrap gap-2">
                {ECART_MOTIFS.map((m) => (
                  <button key={m.key} onClick={() => setEntry({ motif: m.key })} data-testid={`ecart-motif-${m.key}`}
                    className={`px-3 h-9 rounded-md border text-[13px] ${entry.motif === m.key ? 'vd-selected border-gold' : 'border-hair hover:bg-surface'}`}>{m.label}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button className="flex-1" style={{ height: 48 }} onClick={() => { setEntry({ counted: current.theoretical, motif: null }); next(); }} data-testid="inv-conforme"><Check className="w-4 h-4" /> Conforme</Button>
            <Button variant="primary" className="flex-1" style={{ height: 48 }} disabled={ecart !== 0 && !entry.motif} onClick={next} data-testid="inv-next">Casier suivant</Button>
          </div>
        </div>
      )}
    </div>
  );
}
